import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, type ModalOptions, NewFolderLineIcon, Row, Upload2LineIcon, message } from 'ui';
import { AttachmentFilePicker } from 'features/attachments';
import { CreateFolderModal, type CreateFolderModalOptions } from '../create-folder-modal';
import { useCreateAssetMutation } from '../queries';

type P = {
  communityId?: string;
  parentId?: string;
  programId?: string;
};

const AssetsHeaderButtons = ({ communityId, parentId, programId }: P) => {
  const { t } = useTranslation();
  const [createFolderOptions, setCreateFolderOptions] = useState<ModalOptions<CreateFolderModalOptions>>({
    isOpened: false,
  });
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false);
  const { mutateAsync: uploadFile } = useCreateAssetMutation();

  const onFileUpload = (values: Array<Blob | File>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Dropbox picks give raw Blobs with a `.name` tacked on alongside real Files; the mutation only reads `.name`
    const files = values as File[];

    setIsLoadingUploadFile(true);

    void Promise.allSettled(
      files.map(async (file) => {
        const uploadedFile = await uploadFile({ communityId, file, parentId: parentId ?? null, programId });

        message.success(t('assetUploadSuccess', { asset: uploadedFile.name }));
      }),
    ).finally(() => {
      setIsLoadingUploadFile(false);
    });
  };

  return (
    <>
      <Row gutter="lg">
        <Col>
          <Button
            icon={<NewFolderLineIcon />}
            onClick={() => {
              setCreateFolderOptions({ communityId, isOpened: true, parentId, programId });
            }}
            size="small"
            type="link-primary"
          >
            Create Folder
          </Button>
        </Col>
        <Col>
          <AttachmentFilePicker
            icon={<Upload2LineIcon />}
            onChange={onFileUpload}
            size="small"
            type="link-primary"
            uploading={isLoadingUploadFile}
          >
            Upload File
          </AttachmentFilePicker>
        </Col>
      </Row>
      <CreateFolderModal
        onCancel={() => {
          setCreateFolderOptions({ isOpened: false });
        }}
        options={createFolderOptions}
      />
    </>
  );
};

export default AssetsHeaderButtons;
